import { useEffect, useState, useRef } from "react";

/**
 * Hook to detect if component is rendered inside a FilterChip popover
 *
 * FEATURE CONTEXT: This hook was created to support the FilterChips feature where
 * user filters are displayed as compact chips that open in popovers. When filters
 * are rendered inside these popovers, they need to:
 * 1. Hide their title sections (to avoid duplicate titles with the popover header)
 * 2. Remove their individual "Clear" buttons (popover has a cancel "X" button)
 * 3. Provide a cleaner, more compact UI experience
 *
 * WORKAROUND: This hook uses DOM traversal to detect if the component is rendered
 * inside a FilterChip popover. While not the most elegant solution, it's the most
 * practical approach for our current architecture without major refactoring.
 *
 *
 * This DOM-based approach is:
 * - Simple and self-contained
 * - Works with our existing FilterChip architecture
 * - Doesn't require changes to parent components
 * - Minimal performance impact (runs once per component mount)
 *
 * We'll continue with this solution as it effectively solves the problem
 * without over-engineering the architecture.
 */
export const useInsideFilterChip = () => {
  const [insideChip, setInsideChip] = useState(false);
  const elementRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const checkIfInsideChip = () => {
      if (elementRef.current) {
        // Check if element is inside a FilterChip by looking for an element with id starting with "filter-chip-"
        const chipContainer = elementRef.current.closest(
          '[id^="filter-chip-"]'
        );
        setInsideChip(!!chipContainer);
      }
    };

    checkIfInsideChip();

    // Also check after a small delay to ensure DOM is ready
    const timer = setTimeout(checkIfInsideChip, 50);

    return () => clearTimeout(timer);
  }, []);

  return { insideChip, elementRef };
};
